import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../../../components/ui/table';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../../../components/ui/dropdown-menu';
import { Badge } from '../../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription, DialogClose
} from '../../../components/ui/dialog';
import { 
  Tabs, TabsContent, TabsList, TabsTrigger 
} from '../../../components/ui/tabs';
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from '../../../components/ui/card';
import { 
  Eye, 
  Edit, 
  Trash2, 
  MoreHorizontal, 
  Plus, 
  Search, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  EyeOff,
  Filter, 
  Check as CheckIcon, 
  X as XIcon,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useToast } from '../../../components/ui/use-toast';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { 
  fetchAdminBlogPosts, 
  fetchAdminBlogCategories,
  BlogPost, 
  BlogCategory,
  deleteBlogPost 
} from '../../../services/blogService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { useDomainQuery } from '../../../hooks/useDomainQuery';

export const BlogPostList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { applyDomainFilter, applyDomainOnlyFilter, getFilterParams } = useDomainQuery();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'published' | 'draft'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);
  const [deletingPost, setDeletingPost] = useState(false);
  const [editingStatus, setEditingStatus] = useState<{postId: string | null, newStatus?: 'draft' | 'published'}>({ postId: null });
  const [editingDate, setEditingDate] = useState<{postId: string | null, value?: string}>({ postId: null });
  const [editingCategories, setEditingCategories] = useState<{postId: string | null}>({ postId: null });
  const [editingTitle, setEditingTitle] = useState<{postId: string | null, value: string}>({ postId: null, value: '' });
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Record<string, string[]>>({});
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    totalViews: 0,
    totalLikes: 0
  });

  // Récupérer les catégories
  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['adminBlogCategories'],
    queryFn: fetchAdminBlogCategories
  });

  const { data: posts, error, isLoading, refetch } = useQuery<BlogPost[]>({
    queryKey: ['adminBlogPosts', searchQuery, viewMode, selectedCategory],
    queryFn: () => fetchAdminBlogPosts(viewMode, searchQuery, selectedCategory),
  });

  // Utiliser useEffect pour gérer la logique onSuccess
  useEffect(() => {
    if (posts) {
      // Calculer les statistiques à partir des données
      const calculatedStats = {
        total: posts.length,
        published: posts.filter(post => post.status === 'published').length,
        draft: posts.filter(post => post.status === 'draft').length,
        totalViews: posts.reduce((sum, post) => sum + (post.views_count || 0), 0),
        totalLikes: posts.reduce((sum, post) => sum + (post.likes_count || 0), 0)
      };
      setStats(calculatedStats);
    }
  }, [posts]);

  const handleDeleteClick = (post: BlogPost) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;
    
    setDeletingPost(true);
    try {
      await deleteBlogPost(postToDelete.id);

      toast({
        title: 'Succès',
        description: 'L\'article a été supprimé',
      });

      // Mettre à jour la liste des articles
      refetch(); // Rafraîchir la liste des articles
      setDeleteDialogOpen(false); // Fermer la boîte de dialogue
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'article:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer l\'article',
        variant: 'destructive',
      });
    } finally {
      setDeletingPost(false);
    }
  };

  const updatePostTitle = async (postId: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    
    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ 
          title: newTitle,
          updated_at: new Date().toISOString()
        })
        .eq('id', postId);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Le titre a été mis à jour',
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du titre:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le titre',
        variant: 'destructive',
      });
    } finally {
      setEditingTitle({ postId: null, value: '' });
    }
  };

  const updatePostCategories = async (postId: string, categoryIds: string[]) => {
    try {
      // Supprimer toutes les catégories existantes pour cet article
      const { error: deleteError } = await supabase
        .from('blog_post_categories')
        .delete()
        .eq('post_id', postId);

      if (deleteError) throw deleteError;

      // Ajouter les nouvelles catégories
      if (categoryIds.length > 0) {
        const { error: insertError } = await supabase
          .from('blog_post_categories')
          .insert(categoryIds.map(categoryId => ({
            post_id: postId,
            category_id: categoryId
          })));

        if (insertError) throw insertError;
      }

      toast({
        title: 'Succès',
        description: 'Les catégories ont été mises à jour',
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      console.error('Erreur lors de la mise à jour des catégories:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les catégories',
        variant: 'destructive',
      });
    } finally {
      setEditingCategories({ postId: null });
    }
  };

  const updatePostDate = async (postId: string, newDate: Date) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ 
          published_at: newDate.toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', postId);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'La date de publication a été mise à jour',
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la date:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour la date de publication',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (post: BlogPost, newStatus: 'draft' | 'published') => {
    try {
      const updates = {
        status: newStatus,
        published_at: newStatus === 'published' ? new Date().toISOString() : post.published_at,
        updated_at: new Date().toISOString()
      };

      // Mise à jour optimiste de l'interface utilisateur
      const previousPosts = queryClient.getQueryData<BlogPost[]>(['adminBlogPosts', viewMode, searchQuery, selectedCategory]);
      
      if (previousPosts) {
        queryClient.setQueryData(
          ['adminBlogPosts', viewMode, searchQuery, selectedCategory],
          previousPosts.map(p => 
            p.id === post.id 
              ? { ...p, status: newStatus, published_at: updates.published_at, updated_at: updates.updated_at }
              : p
          )
        );
      }

      const { error } = await supabase
        .from('blog_posts')
        .update(updates)
        .eq('id', post.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: `L'article est maintenant ${newStatus === 'published' ? 'publié' : 'en brouillon'}`,
      });

      // Mettre à jour les statistiques
      if (post.status !== newStatus) {
        setStats({
          ...stats,
          published: newStatus === 'published' ? stats.published + 1 : Math.max(0, stats.published - 1),
          draft: newStatus === 'draft' ? stats.draft + 1 : Math.max(0, stats.draft - 1)
        });
      }
      
      // Rafraîchir les données
      await refetch();
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le statut de l\'article',
        variant: 'destructive',
      });
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      // Récupérer l'article original
      const { data: original, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!original) return;

      // Créer une copie avec nouvel ID et nouveau slug
      const duplicate = { 
        ...original, 
        id: uuidv4(),
        slug: `${original.slug}-copie-${Date.now()}`,
        created_at: new Date().toISOString()
      };

      // Insérer la copie dans la base de données
      const { error: insertError } = await supabase
        .from('blog_posts')
        .insert([duplicate]);

      if (insertError) throw insertError;

      toast({
        title: 'Succès',
        description: 'Article dupliqué avec succès',
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de dupliquer l\'article',
        variant: 'destructive',
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Gestion des articles</h1>
            <p className="text-gray-400">Gérez les articles publiés sur votre blog</p>
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'all' | 'published' | 'draft')} className="w-full md:w-auto">
              <TabsList className="bg-white/5 border border-white/10">
                <TabsTrigger 
                  value="all" 
                  className="text-white data-[state=active]:bg-yellow-500 data-[state=active]:text-black"
                >
                  Tous
                </TabsTrigger>
                <TabsTrigger 
                  value="published" 
                  className="text-white data-[state=active]:bg-yellow-500 data-[state=active]:text-black"
                >
                  Publiés
                </TabsTrigger>
                <TabsTrigger 
                  value="draft" 
                  className="text-white data-[state=active]:bg-yellow-500 data-[state=active]:text-black"
                >
                  Brouillons
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            {/* Sélecteur de catégories */}
            <div className="relative">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 bg-white/5 border-white/10 text-white">
                    <Filter size={16} />
                    {selectedCategory ? 
                      categories?.find((cat: BlogCategory) => cat.id === selectedCategory)?.name || 'Catégorie' 
                      : 'Catégorie'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black/95 border border-white/10 text-white">
                  <DropdownMenuItem 
                    onClick={() => setSelectedCategory('')}
                    className={!selectedCategory ? 'bg-white/10' : ''}
                  >
                    Toutes les catégories
                  </DropdownMenuItem>
                  {categories?.map((category: BlogCategory) => (
                    <DropdownMenuItem 
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={selectedCategory === category.id ? 'bg-white/10' : ''}
                    >
                      {category.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <Link to="/admin/blog/create">
              <Button
                variant="default"
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <Plus size={18} className="mr-2" />
                Créer un article
              </Button>
            </Link>
          </div>
        </div>

        {/* Liste des articles */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center p-12">
              <Clock className="mr-2 h-4 w-4 animate-spin" />
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
              <h3 className="text-lg font-medium text-white mb-2">Erreur lors du chargement des articles</h3>
              <p className="text-gray-400 mb-4">{(error as Error).message}</p>
            </div>
          ) : posts?.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="mx-auto mb-4 text-gray-400" size={48} />
              <h3 className="text-lg font-medium text-white mb-2">Aucun article</h3>
              <p className="text-gray-400 mb-4">
                {searchQuery
                  ? `Aucun résultat pour "${searchQuery}"`
                  : viewMode !== 'all'
                  ? `Aucun article ${viewMode === 'published' ? 'publié' : 'en brouillon'}`
                  : 'Commencez par créer votre premier article'}
              </p>
              <Link to="/admin/blog/create">
                <Button variant="outline" className="mt-4">
                  <Plus size={18} className="mr-2" />
                  Créer un article
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Titre
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Catégories
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {posts.map((post: any, index) => (
                    <tr key={post.id} className={`hover:bg-white/10 group ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingTitle.postId === post.id ? (
                          <div className="relative">
                            <input
                              type="text"
                              value={editingTitle.value}
                              onChange={(e) => setEditingTitle({ postId: post.id, value: e.target.value })}
                              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                              autoFocus
                            />
                            <div className="absolute right-0 top-0 mt-1 flex space-x-1">
                              <button
                                onClick={() => setEditingTitle({ postId: null, value: '' })}
                                className="p-1 text-gray-400 hover:text-white"
                                title="Annuler"
                              >
                                <XIcon size={16} />
                              </button>
                              <button
                                onClick={() => updatePostTitle(post.id, editingTitle.value)}
                                className="p-1 text-green-400 hover:text-green-300"
                                title="Enregistrer"
                              >
                                <CheckIcon size={16} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="flex items-center cursor-pointer group"
                            onClick={() => setEditingTitle({ postId: post.id, value: post.title })}
                          >
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-400/10 rounded-full flex items-center justify-center">
                              <FileText className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white truncate max-w-[250px] group-hover:underline" title={post.title}>
                                {post.title}
                              </div>
                              <div className="text-sm text-gray-400 mt-1 truncate max-w-[250px] group-hover:overflow-visible group-hover:whitespace-normal group-hover:text-clip" title={post.excerpt || 'Pas d\'extrait'}>
                                {post.excerpt || 'Pas d\'extrait'}
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingStatus.postId === post.id ? (
                          <div className="relative">
                            <select
                              value={editingStatus.newStatus || post.status}
                              onChange={(e) => {
                                const newStatus = e.target.value as 'draft' | 'published';
                                setEditingStatus(prev => ({
                                  ...prev,
                                  newStatus
                                }));
                              }}
                              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm pr-10"
                              autoFocus
                            >
                              <option value="draft">Brouillon</option>
                              <option value="published">Publié</option>
                            </select>
                            <div className="absolute right-0 top-0 mt-1 flex space-x-1">
                              <button
                                onClick={() => setEditingStatus({ postId: null })}
                                className="p-1 text-gray-400 hover:text-white"
                                title="Annuler"
                              >
                                <XIcon size={16} />
                              </button>
                              <button
                                onClick={async () => {
                                  if (editingStatus.newStatus) {
                                    await handleStatusChange(post, editingStatus.newStatus);
                                  }
                                  setEditingStatus({ postId: null });
                                }}
                                className="p-1 text-green-400 hover:text-green-300"
                                title="Enregistrer"
                              >
                                <CheckIcon size={16} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingStatus({ 
                                postId: post.id,
                                newStatus: post.status 
                              });
                            }}
                          >
                            <Badge
                              className={
                                post.status === 'published'
                                  ? 'bg-green-400/20 text-green-400 border-green-400/20 cursor-pointer hover:bg-green-400/30'
                                  : 'bg-gray-400/20 text-gray-400 border-gray-400/20 cursor-pointer hover:bg-gray-400/30'
                              }
                            >
                              {post.status === 'published' ? 'Publié' : 'Brouillon'}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingDate.postId === post.id ? (
                          <div className="relative">
                            <input
                              type="datetime-local"
                              value={editingDate.value || (post.published_at ? new Date(post.published_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16))}
                              onChange={(e) => setEditingDate(prev => ({
                                ...prev,
                                value: e.target.value
                              }))}
                              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm pr-16"
                              autoFocus
                            />
                            <div className="absolute right-0 top-0 mt-1 flex space-x-1">
                              <button
                                onClick={() => setEditingDate({ postId: null })}
                                className="p-1 text-gray-400 hover:text-white"
                                title="Annuler"
                              >
                                <XIcon size={16} />
                              </button>
                              <button
                                onClick={async () => {
                                  const newDate = new Date(editingDate.value || post.published_at || new Date());
                                  await updatePostDate(post.id, newDate);
                                  setEditingDate({ postId: null });
                                }}
                                className="p-1 text-green-400 hover:text-green-300"
                                title="Enregistrer"
                              >
                                <CheckIcon size={16} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="text-sm text-gray-400 cursor-pointer hover:bg-white/5 px-2 py-1 rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingDate({ 
                                postId: post.id,
                                value: post.published_at ? new Date(post.published_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16)
                              });
                            }}
                          >
                            {post.status === 'published' && post.published_at
                              ? format(new Date(post.published_at), 'dd MMM yyyy HH:mm', { locale: fr })
                              : format(new Date(post.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                          </div>
                        )}
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 relative"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCategories({ postId: post.id });
                          setSelectedCategoryIds(prev => ({
                            ...prev,
                            [post.id]: post.categories?.map((cat: any) => cat.id) || []
                          }));
                        }}
                      >
                        {editingCategories.postId === post.id ? (
                          <div 
                            className="absolute left-0 top-0 z-50 bg-gray-900 border border-gray-700 rounded-lg p-4 shadow-lg w-64"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {categories?.map((category: any) => (
                                <div key={category.id} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`cat-${post.id}-${category.id}`}
                                    checked={selectedCategoryIds[post.id]?.includes(category.id) || false}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setSelectedCategoryIds(prev => ({
                                        ...prev,
                                        [post.id]: isChecked
                                          ? [...(prev[post.id] || []), category.id]
                                          : (prev[post.id] || []).filter((id: string) => id !== category.id)
                                      }));
                                    }}
                                    className="h-4 w-4 text-yellow-500 rounded border-gray-600 bg-gray-700 focus:ring-yellow-500"
                                  />
                                  <label 
                                    htmlFor={`cat-${post.id}-${category.id}`}
                                    className="ml-2 text-sm text-gray-300 cursor-pointer"
                                  >
                                    {category.name}
                                  </label>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 flex justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingCategories({ postId: null })}
                                className="text-xs"
                              >
                                Annuler
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={async () => {
                                  await updatePostCategories(
                                    post.id, 
                                    selectedCategoryIds[post.id] || []
                                  );
                                }}
                                className="bg-yellow-500 text-black hover:bg-yellow-600 text-xs"
                              >
                                Enregistrer
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="truncate max-w-[150px] hover:overflow-visible hover:whitespace-normal hover:text-clip hover:bg-gray-800 hover:z-10 hover:absolute hover:p-2 hover:rounded-md cursor-pointer"
                            title={post.categories && post.categories.length > 0 
                              ? post.categories.map((cat: any) => cat.name).join(', ') 
                              : 'Cliquez pour ajouter des catégories'}
                          >
                            {post.categories && post.categories.length > 0 
                              ? post.categories.map((cat: any) => cat.name).join(', ')
                              : <span className="text-gray-500 italic">Ajouter des catégories</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStatusChange(
                              post, 
                              post.status === 'published' ? 'draft' : 'published'
                            )}
                            className="text-yellow-400 hover:text-yellow-300"
                          >
                            {post.status === 'published' ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </Button>
                          {post.status === 'published' && (
                            <a 
                              href={`/blog/${post.slug}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                                <Eye size={18} />
                              </Button>
                            </a>
                          )}
                          <Link to={`/admin/blog/edit/${post.id}`}>
                            <Button variant="ghost" size="sm" className="text-yellow-400 hover:text-yellow-300">
                              <Edit size={18} />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(post.id)}
                            className="text-green-400 hover:text-green-300"
                          >
                            <Copy size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(post)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Supprimer l'article</DialogTitle>
            <DialogDescription className="text-gray-400">
              Êtes-vous sûr de vouloir supprimer cet article ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          {postToDelete && (
            <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-blue-400 mr-2" />
                <span className="font-medium text-white">{postToDelete.title}</span>
              </div>
              {postToDelete.excerpt && (
                <p className="mt-2 text-sm text-gray-400 line-clamp-2">{postToDelete.excerpt}</p>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deletingPost}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deletingPost ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default BlogPostList;
